import { translateKey } from 'src/locales/translate';
// @mui

import { Dialog, DialogTitle, Link, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { conversationApi } from 'src/api/conversation';
import Label from 'src/components/label';
import OrganizationLink from 'src/components/organization-link/OrganizationLink';
import ServiceBadge from 'src/components/service-badge';
import VisitPaymentStatusBadge from 'src/components/visit-payment-status-badge/VisitPaymentStatusBadge';
import VisitStatusBadge from 'src/components/visit-status-badge/VisitStatusBadge';
import VisitTypeBadge from 'src/components/visit-type-badge/VisitTypeBadge';
import VisitWalkInBadge from 'src/components/visit-walk-in-badge/VisitWalkInBadge';
import usePrevious from 'src/hooks/usePrevious';
import { useResponsiveBreakpoints } from 'src/hooks/useResponsiveBreakpoints';
import {
  GetVisitById,
  GetVisitByIdStatusEnum,
  GetVisitByIdVisitTypeEnum,
} from 'woofwoof-api';
import VisitModalTabs from './VisitModalTabs';
// ---------------------------------------------------------------------

type VisitModalProps = {
  isOpen: boolean;
  onClose: () => void;
  visit?: GetVisitById;
  onReschedule?: ({
    professionalId,
    date,
    visitId,
  }: {
    professionalId: string;
    date: string;
    visitId: string;
  }) => void;
  isRescheduleLoading?: boolean;
  onCancel?: () => void;
  isCancelLoading?: boolean;
  editOnly?: boolean;
  onReject?: (visitId: string) => void;
  onApprove?: (visit: GetVisitById) => void;
  isApprovalLoading?: boolean;
  onJoinVideoOrPay?: (visitId: string) => void;
  /**
   * Optional refetch hook fired after a successful "Edytuj szczegóły"
   * submit from inside the modal's Szczegóły footer.
   */
  onDetailsEdited?: () => void;
};

export default function VisitModal({
  isOpen,
  onClose,
  visit,
  onReschedule,
  isRescheduleLoading,
  onCancel,
  isCancelLoading,
  editOnly,
  onReject,
  onApprove,
  isApprovalLoading,
  onJoinVideoOrPay,
  onDetailsEdited,
}: VisitModalProps) {
  const prevIsOpen = usePrevious(isOpen);
  const [visitCopy, setVisitCopy] = useState<GetVisitById | undefined>(visit);
  const { isMd, isSm } = useResponsiveBreakpoints();

  useEffect(() => {
    if (visit && isOpen) {
      setVisitCopy(visit);
    }
  }, [visit, isOpen]);

  const isVisitTypeVideo =
    visitCopy?.visitType === GetVisitByIdVisitTypeEnum.Video;
  const isVisitCompleted =
    visitCopy?.status === GetVisitByIdStatusEnum.Completed;
  const isVisitCancelled =
    visitCopy?.status === GetVisitByIdStatusEnum.Cancelled ||
    visitCopy?.status === GetVisitByIdStatusEnum.Aborted;
  const isVisitPending = visitCopy?.status === GetVisitByIdStatusEnum.Pending;

  const { isLoading: isConversationLoading, remove: removeConversation } =
    useQuery(
      ['conversationDetailsModal'],
      () =>
        conversationApi.conversationControllerGetConversationById({
          conversationId: (visitCopy?.conversation as any)?.id,
        }),
      {
        retry: 1,
        enabled: isVisitCompleted || isVisitCancelled,
      },
    );

  useEffect(() => {
    if (!isOpen && prevIsOpen) {
      removeConversation();

      setTimeout(() => {
        setVisitCopy(undefined);
      }, 150);
    }
  }, [removeConversation, isOpen, prevIsOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 48px)' },
          maxWidth: '900px',
          mx: 0,
          my: { xs: 2, sm: 4 },
        },
      }}
    >
      <>
        <DialogTitle component="div">
          <Stack
            sx={{
              flexDirection: isMd ? 'column' : 'row',
              gap: 1,
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flex: 1,
            }}
          >
            <Stack>
              <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                <Typography variant="h6">
                  {visitCopy?.startTime &&
                    format(new Date(visitCopy.startTime), 'dd/MM/yyyy H:mm')}
                </Typography>
              </Stack>
              <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                <OrganizationLink
                  name={visitCopy?.organization?.name}
                  slug={visitCopy?.organization?.slug}
                  id={visitCopy?.organization?.id}
                  color="text.secondary"
                />
              </Stack>
              {visitCopy?.visitAddress && (
                <Stack>
                  <Label
                    variant="soft"
                    color="success"
                    sx={{ textTransform: 'none', width: 'fit-content', mt: 1 }}
                  >
                    <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                      <Typography
                        sx={{ color: 'inherit', font: 'inherit' }}
                        component="span"
                      >{translateKey("visit.visitAddressLabelColon")}</Typography>
                      <Link
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          `${visitCopy?.visitAddress?.street}, ${visitCopy?.visitAddress?.zipCode} ${visitCopy?.visitAddress?.city}`,
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textDecoration: 'underline',
                          color: 'inherit',
                        }}
                      >
                        {`${visitCopy?.visitAddress?.street}, ${visitCopy?.visitAddress?.zipCode} ${visitCopy?.visitAddress?.city}`}
                      </Link>
                    </Stack>
                  </Label>
                </Stack>
              )}
            </Stack>
            <Stack sx={{ gap: 1 }}>
              <Stack
                sx={{
                  flexDirection: isMd ? 'column' : 'row',
                  gap: 1,
                  justifyContent: isMd ? 'flex-start' : 'flex-end',
                  flexWrap: 'wrap',
                }}
              >
                {visitCopy?.visitType && (
                  <VisitTypeBadge visitType={visitCopy.visitType} />
                )}
                <VisitWalkInBadge origin={visitCopy?.origin} />
                {visitCopy?.service && (
                  <ServiceBadge
                    serviceName={visitCopy.service.name}
                    duration={visitCopy.service.duration}
                    price={visitCopy.service.price}
                    visitType={visitCopy.visitType}
                    showPrice
                  />
                )}
              </Stack>
              <Stack
                sx={{
                  justifyContent: isMd ? 'flex-start' : 'flex-end',
                  flexDirection: isMd ? 'column' : 'row',
                }}
              >
                {isVisitTypeVideo ? (
                  isVisitCompleted || isVisitCancelled || isVisitPending ? (
                    <VisitStatusBadge
                    visitStatus={visitCopy?.status}
                    rejectedBy={visitCopy?.rejectedBy}
                  />
                  ) : (
                    <VisitPaymentStatusBadge
                      paymentStatus={visitCopy.payment?.status}
                    />
                  )
                ) : (
                  <VisitStatusBadge
                    visitStatus={visitCopy?.status}
                    rejectedBy={visitCopy?.rejectedBy}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </DialogTitle>
        <VisitModalTabs
          visit={visitCopy}
          isConversationLoading={isConversationLoading}
          onClose={onClose}
          isOpen={isOpen}
          onReschedule={onReschedule}
          isRescheduleLoading={isRescheduleLoading}
          onCancel={onCancel}
          isCancelLoading={isCancelLoading}
          editOnly={editOnly}
          onReject={onReject}
          onApprove={onApprove}
          isApprovalLoading={isApprovalLoading}
          onJoinVideoOrPay={onJoinVideoOrPay}
          onDetailsEdited={onDetailsEdited}
        />
      </>
    </Dialog>
  );
}
